import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Slider from "react-slick";
import ReactECharts from "echarts-for-react";
import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import { IconButton } from "@mui/material";
import API_URL from "../../utils/API_URL";
import GraphDialog from "./GraphDialog";
import { Icon } from "../icons";


const MchUtilizationChart = ({ sendMessage }) => {
  const [chartData, setChartData] = useState(null);
  const [standardValue, setStandardValue] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { group_code, project_code } = useParams();

  const handleOpenDialog = () => {
    sendMessage("Canvas", "SetCaptureAllKeyboardInput", "false");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    sendMessage("Canvas", "SetCaptureAllKeyboardInput", "true");
    setIsDialogOpen(false);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.post(API_URL + "/productivity/getMchUtilizationData", {
        group_code,
        project_code,
      });
      
      const { chartData: data, standardValue: apiStandardValue } = response.data;
      setStandardValue(apiStandardValue);

      if (data && data.length > 0) {
        const markLineOpt = {
          silent: true,
          symbol: 'none',
          label: {
            position: 'insideEndTop',
            formatter: '{b}: {c}%',
            color: '#fff',
          },
          lineStyle: {
            type: 'dashed',
            color: '#FF6000'
          },
          data: [{
            yAxis: apiStandardValue,
            name: '기준'
          }]
        };

        const chartOption = {
          title: {
            text: '장비 가동실적',
            left: 'center',
            textStyle: { color: '#fff' }
          },
          tooltip: { 
            trigger: 'axis',
            formatter: function (params) {
              let result = params[0].name + '<br/>';
              params.forEach(function (item) {
                result += item.marker + " " + item.seriesName + ": " + item.value + "%<br/>";
              });
              return result;
            }
          },
          legend: {
            data: ['가동률', '고장률', '불량률'],
            top: 30,
            textStyle: { color: '#fff' }
          },
          color: ['#5470C6', '#FF6000', '#EE6666'], // 색상 팔레트 변경
          xAxis: {
            type: 'category',
            data: data.map(item => item.mch_name),
            axisLabel: { color: '#fff' }
          },
          yAxis: {
            type: 'value',
            min: 0,
            max: 100,
            axisLabel: { 
              color: '#fff',
              formatter: '{value}%' // Y축에 % 추가
            }
          },
          series: [
            { 
              name: '가동률', 
              type: 'bar', 
              data: data.map(item => item.util_rate),
              markLine: apiStandardValue > 0 ? markLineOpt : null,
              showBackground: true,
              backgroundStyle: {
                color: 'rgba(180, 180, 180, 0.2)'
              }
            },
            { 
              name: '고장률', 
              type: 'bar', 
              data: data.map(item => item.failure_rate), 
              showBackground: true,
              backgroundStyle: {
                color: 'rgba(180, 180, 180, 0.2)'
            } },
            { 
              name: '불량률', 
              type: 'bar', 
              data: data.map(item => item.defect_rate), 
              showBackground: true,
              backgroundStyle: {
                color: 'rgba(180, 180, 180, 0.2)'
            } },
          ],
          dataZoom: [
            { type: 'slider', startValue: 0, endValue: 3 }, // 처음 4개(0~3)의 데이터를 보여줍니다.
            { type: 'inside', startValue: 0, endValue: 3 }
          ],
          grid: {
            top: '25%', // legend와의 간격 확보
            left: '3%',
            right: '4%',
            bottom: '15%',
            containLabel: true
          },
        };
        setChartData(chartOption);
      } else {
        setChartData(null);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [group_code, project_code]);

  if (loading) return <div style={{color: 'white', textAlign: 'center', paddingTop: '100px'}}>로딩 중...</div>;
  if (error) return <div style={{color: 'red', textAlign: 'center', paddingTop: '100px'}}>에러 발생: {error.message}</div>;

  return chartData ? (
    <div style={{ position: 'relative', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ position: 'absolute', top: 0, right: 5, zIndex: 10 }}>
        <IconButton onClick={handleOpenDialog} size="small" title="기준값 설정">
          <Icon name="highlightIcon" style={{ width: '20px', height: '20px', '--stroke-width': '3px', color: '#d5d5d5' }} />
        </IconButton>
      </div>
      <ReactECharts option={chartData} style={{height: '100%'}} />
      <GraphDialog 
        open={isDialogOpen}
        onClose={handleCloseDialog}
        category="mch_utilization"
        currentValue={standardValue}
        onSaveSuccess={fetchData}
      />
    </div>
  ) : <div style={{color: 'white', textAlign: 'center', paddingTop: '100px'}}>데이터가 없습니다.</div>;
}

const UphChart = ({ sendMessage }) => {
  const [chartData, setChartData] = useState(null);
  const [standardValue, setStandardValue] = useState(null);
  const [averageValue, setAverageValue] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { group_code, project_code } = useParams();

  const handleOpenDialog = () => {
    sendMessage("Canvas", "SetCaptureAllKeyboardInput", "false");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    sendMessage("Canvas", "SetCaptureAllKeyboardInput", "true");
    setIsDialogOpen(false);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.post(API_URL + "/productivity/getUphData", {
        group_code,
        project_code,
      });
      
      const { chartData: data, standardValue: apiStandardValue, averageUph: apiAverageValue } = response.data;
      setStandardValue(apiStandardValue);
      setAverageValue(apiAverageValue);

      if (data && data.length > 0) {
        // 데이터를 lot_name 별로 그룹화하여 시리즈를 생성하는 안정적인 방식으로 변경합니다.
        const dataByLot = data.reduce((acc, item) => {
          const lotName = item.lot_name;
          if (!acc[lotName]) {
            acc[lotName] = [];
          }
          acc[lotName].push([item.timedata, parseFloat(item.uph_value) || 0]);
          return acc;
        }, {});

        const series = Object.keys(dataByLot).map((lotName, index) => {
          const markLineData = [];
          if (index === 0) { // 첫 번째 시리즈에만 마크라인 추가
            if (apiStandardValue > 0) {
              markLineData.push({
                name: '기준',
                yAxis: apiStandardValue,
                lineStyle: { type: 'dashed', color: '#FF6000' },
              });
            }
            if (apiAverageValue !== null) {
              markLineData.push({
                name: '평균',
                yAxis: apiAverageValue,
                lineStyle: { type: 'solid', color: '#007bff' },
              });
            }
          }

          return {
            name: lotName,
            type: 'line',
            smooth: true,
            areaStyle: {},
            data: dataByLot[lotName],
            markLine: markLineData.length > 0 ? {
              silent: true,
              symbol: 'none',
              label: {
                position: 'insideEndTop',
                formatter: '{b}: {c}',
                color: '#fff',
              },
              data: markLineData
            } : null
          };
        });

        const chartOption = {
          title: { text: '시간별 UPH', left: 'center', textStyle: { color: '#fff' } },
          tooltip: { trigger: 'axis' },
          legend: { 
            data: Object.keys(dataByLot),
            top: 30, 
            textStyle: { color: '#fff' }, 
            type: 'scroll' 
          },
          xAxis: { 
            type: 'time', 
            axisLabel: { 
              color: '#fff',
              formatter: (value) => {
                const date = new Date(value);
                const hours = date.getHours();
                const minutes = date.getMinutes();
                if (hours === 0 && minutes === 0) {
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
              }
            } 
          },
          yAxis: { 
            type: 'value', 
            axisLabel: { color: '#fff' },
            min: 0,
            max: 100
          },
          series: series, // dataset을 사용하지 않고, 가공된 series를 직접 사용합니다.
          grid: { top: '25%', left: '3%', right: '4%', bottom: '15%', containLabel: true },
          dataZoom: [
            { type: 'slider', start: 0, end: 100 },
            { type: 'inside', start: 0, end: 100 }
          ],
        };
        setChartData(chartOption);
      } else {
        setChartData(null);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchData();
  }, [group_code, project_code]);

  if (loading) return <div style={{color: 'white', textAlign: 'center', paddingTop: '100px'}}>로딩 중...</div>;
  if (error) return <div style={{color: 'red', textAlign: 'center', paddingTop: '100px'}}>에러 발생: {error.message}</div>;

  return chartData ? (
    <div style={{ position: 'relative', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ position: 'absolute', top: 0, right: 5, zIndex: 10 }}>
        <IconButton onClick={handleOpenDialog} size="small" title="기준값 설정">
          <Icon name="highlightIcon" style={{ width: '20px', height: '20px', '--stroke-width': '3px', color: '#d5d5d5' }} />
        </IconButton>
      </div>
      <ReactECharts option={chartData} style={{height: '100%'}} />
      <GraphDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        category="uph"
        currentValue={standardValue}
        onSaveSuccess={fetchData}
      />
    </div>
  ) : <div style={{color: 'white', textAlign: 'center', paddingTop: '100px'}}>데이터가 없습니다.</div>;
};


const ProductivityChart = ({ sendMessage }) => {
  const charts = [
    <MchUtilizationChart key="mch_util" sendMessage={sendMessage} />,
    <UphChart key="uph" sendMessage={sendMessage} />,
  ];
  
  // 나머지 코드는 유지하되, charts 배열만 실제 차트 컴포넌트로 교체합니다.
  const settings = {
    dots: false,
    infinite: charts.length > 4,
    arrows: charts.length > 4,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: charts.length > 3,
          arrows: charts.length > 3,
          dots: false
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 2,
          infinite: charts.length > 2,
          arrows: charts.length > 2,
        }
      },
      {
        breakpoint: 800,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: charts.length > 1,
          arrows: charts.length > 1,
        }
      }
    ]
  };


  const customSlickStyles = `
    .slick-slider, .slick-list, .slick-track {
      height: 100%;
    }
    /* slick-slide 내부의 div들도 높이를 100%로 설정하여 그래프 높이 문제를 해결합니다. */
    .slick-slide > div {
      height: 100%;
    }
    .slick-slide > div > div {
      height: 100%;
    }
    .slick-prev, .slick-next {
      z-index: 1;
    }
    .slick-prev {
      left: 0px;
    }
    .slick-next {
      right: 0px;
    }
  `;

  return (
    <>
      <style>{customSlickStyles}</style>
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0, 
          right: 0,
          height: "300px",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          padding: "20px",
          boxSizing: "border-box",
          zIndex: 1000,
        }}
      >
        <Slider {...settings}>
          {charts.map((chart, index) => (
            <div key={index}>{chart}</div>
          ))}
        </Slider>
      </div>
    </>
  );
};

export default ProductivityChart; 